<template>
  <div class="caisse-analytics">
    <div v-if="loading" class="text-center py-5">
      <a-spin size="large" />
    </div>
    <div v-else>
      <!-- Summary Cards -->
      <div class="row mb-4">
        <div class="col-md-3 col-sm-6 mb-3">
          <div class="stat-card">
            <div class="stat-icon caisses">
              <a-icon type="bank" />
            </div>
            <div class="stat-content">
              <div class="stat-value">{{ data && data.summary && data.summary.totalCaisses || 0 }}</div>
              <div class="stat-label">{{ $t('financialStats.totalCaisses') }}</div>
            </div>
          </div>
        </div>
        <div class="col-md-3 col-sm-6 mb-3">
          <div class="stat-card">
            <div class="stat-icon revenue">
              <a-icon type="dollar" />
            </div>
            <div class="stat-content">
              <div class="stat-value">{{ formatCurrency(data && data.summary && data.summary.totalRevenue) }}</div>
              <div class="stat-label">{{ $t('financialStats.totalRevenue') }}</div>
            </div>
          </div>
        </div>
        <div class="col-md-3 col-sm-6 mb-3">
          <div class="stat-card">
            <div class="stat-icon invoices">
              <a-icon type="file-text" />
            </div>
            <div class="stat-content">
              <div class="stat-value">{{ data && data.summary && data.summary.totalInvoices || 0 }}</div>
              <div class="stat-label">{{ $t('financialStats.totalInvoices') }}</div>
            </div>
          </div>
        </div>
        <div class="col-md-3 col-sm-6 mb-3">
          <div class="stat-card">
            <div class="stat-icon average">
              <a-icon type="calculator" />
            </div>
            <div class="stat-content">
              <div class="stat-value">{{ formatCurrency(averageRevenuePerCaisse) }}</div>
              <div class="stat-label">{{ $t('financialStats.averageRevenuePerCaisse') }}</div>
            </div>
          </div>
        </div>
      </div>

      <!-- Caisse Performance Table -->
      <div class="row mb-4">
        <div class="col-12">
          <div class="card">
            <div class="card-header">
              <h6 class="mb-0">{{ $t('financialStats.caissePerformance') }}</h6>
            </div>
            <div class="card-body">
              <a-table
                :columns="caisseColumns"
                :dataSource="data && data.caisses || []"
                :pagination="false"
                size="small"
                :scroll="{ x: 1000 }"
              >
                <template slot="caisseName" slot-scope="text, record">
                  <div class="caisse-info">
                    <div class="caisse-icon" :class="getCaisseTypeClass(record.caisseType)">
                      <a-icon :type="getCaisseTypeIcon(record.caisseType)" />
                    </div>
                    <div class="caisse-details">
                      <div class="caisse-name">{{ text }}</div>
                      <div class="caisse-type">{{ getCaisseTypeName(record.caisseType) }}</div>
                    </div>
                  </div>
                </template>
                <template slot="totalRevenue" slot-scope="text">
                  <span class="amount-value">{{ formatCurrency(text) }}</span>
                </template>
                <template slot="invoiceCount" slot-scope="text">
                  <a-badge :count="text" :number-style="{ backgroundColor: '#1890ff' }" />
                </template>
                <template slot="averageInvoiceValue" slot-scope="text">
                  <span class="amount-value">{{ formatCurrency(text) }}</span>
                </template>
                <template slot="performance" slot-scope="text, record">
                  <div class="performance-indicator">
                    <a-progress
                      :percent="parseFloat(getPerformancePercent(record))"
                      :showInfo="false"
                      :strokeWidth="6"
                      :strokeColor="getPerformanceColor(record)"
                    />
                    <div class="performance-text">
                      {{ getPerformanceText(record) }}
                    </div>
                  </div>
                </template>
                <template slot="monthlyRevenue" slot-scope="text">
                  <div class="mini-chart">
                    <apexchart
                      :options="miniChartOptions"
                      :series="getMiniChartData(text).series"
                      type="line"
                      height="60"
                      class="mini-chart-container"
                    />
                  </div>
                </template>
              </a-table>
            </div>
          </div>
        </div>
      </div>

      <!-- Charts Row -->
      <div class="row">
        <div class="col-lg-8 mb-4">
          <div class="card">
            <div class="card-header">
              <h6 class="mb-0">{{ $t('financialStats.revenueComparison') }}</h6>
            </div>
            <div class="card-body">
              <apexchart
                :options="revenueComparisonChartOptions"
                :series="revenueComparisonChartData.series"
                type="bar"
                height="300"
                class="chart-container"
              />
            </div>
          </div>
        </div>
        
        <div class="col-lg-4 mb-4">
          <div class="card">
            <div class="card-header">
              <h6 class="mb-0">{{ $t('financialStats.caisseDistribution') }}</h6>
            </div>
            <div class="card-body">
              <apexchart
                :options="distributionChartOptions"
                :series="distributionChartData.series"
                type="donut"
                height="300"
                class="chart-container"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getBuildingCurrency } from "@/utilities/countrySpecific";

export default {
  name: "CaisseAnalytics",
  props: {
    loading: {
      type: Boolean,
      default: false
    },
    data: {
      type: Object,
      default: () => ({
        summary: {
          totalCaisses: 0,
          totalRevenue: 0,
          totalInvoices: 0
        },
        caisses: []
      })
    },
    monthMapping: {
      type: Object,
      default: () => ({ months: [], labels: [] })
    }
  },
  computed: {
    averageRevenuePerCaisse() {
      const caisses = this.data && this.data.caisses || [];
      if (caisses.length === 0) return 0;
      const totalRevenue = caisses.reduce((sum, caisse) => sum + caisse.totalRevenue, 0);
      return totalRevenue / caisses.length;
    },
    
    revenueComparisonChartData() {
      const caisses = (this.data && this.data.caisses || []).sort((a, b) => b.totalRevenue - a.totalRevenue);
      const labels = caisses.map(c => c.caisseName.length > 15 ? c.caisseName.substring(0, 15) + '...' : c.caisseName);
      const revenue = caisses.map(c => c.totalRevenue);
      
      return {
        labels,
        series: [{ name: this.$t('financialStats.revenue'), data: revenue }]
      };
    },
    
    distributionChartData() {
      const caisses = this.data && this.data.caisses || [];
      const totalRevenue = caisses.reduce((sum, caisse) => sum + caisse.totalRevenue, 0);
      if (totalRevenue === 0) {
        return {
          series: [],
          labels: []
        };
      }
      
      const caissesWithPercentage = caisses.map(caisse => ({
        ...caisse,
        percentage: ((caisse.totalRevenue / totalRevenue) * 100).toFixed(1)
      })).sort((a, b) => b.totalRevenue - a.totalRevenue);
      
      return {
        series: caissesWithPercentage.map(c => c.totalRevenue),
        labels: caissesWithPercentage.map(c => c.caisseName.length > 10 ? c.caisseName.substring(0, 10) + '...' : c.caisseName)
      };
    },
    
    caisseColumns() {
      return [
        {
          title: this.$t('financialStats.caisse'),
          dataIndex: 'caisseName',
          key: 'caisseName',
          scopedSlots: { customRender: 'caisseName' }
        },
        {
          title: this.$t('financialStats.totalRevenue'),
          dataIndex: 'totalRevenue',
          key: 'totalRevenue',
          scopedSlots: { customRender: 'totalRevenue' }
        },
        {
          title: this.$t('financialStats.invoices'),
          dataIndex: 'invoiceCount',
          key: 'invoiceCount',
          scopedSlots: { customRender: 'invoiceCount' }
        },
        {
          title: this.$t('financialStats.averageInvoiceValue'),
          dataIndex: 'averageInvoiceValue',
          key: 'averageInvoiceValue',
          scopedSlots: { customRender: 'averageInvoiceValue' }
        },
        {
          title: this.$t('financialStats.performance'),
          key: 'performance',
          scopedSlots: { customRender: 'performance' }
        },
        {
          title: this.$t('financialStats.monthlyTrend'),
          dataIndex: 'monthlyRevenue',
          key: 'monthlyRevenue',
          scopedSlots: { customRender: 'monthlyRevenue' }
        },
      ];
    },
    
    revenueComparisonChartOptions() {
      return {
        chart: {
          type: 'bar',
          height: 300,
          toolbar: {
            show: true
          }
        },
        colors: ['#1890ff'],
        plotOptions: {
          bar: {
            horizontal: false,
            columnWidth: '60%',
            borderRadius: 4
          }
        },
        xaxis: {
          categories: this.revenueComparisonChartData.labels,
          labels: {
            style: {
              colors: '#8c8c8c',
              fontSize: '12px'
            }
          }
        },
        yaxis: {
          labels: {
            formatter: (value) => {
              if (value >= 1000000) {
                return (value / 1000000).toFixed(1) + 'M';
              } else if (value >= 1000) {
                return (value / 1000).toFixed(1) + 'K';
              }
              return value;
            },
            style: {
              colors: '#8c8c8c',
              fontSize: '12px'
            }
          }
        },
        tooltip: {
          y: {
            formatter: (value) => {
              return value.toLocaleString() + ` ${this.currency}`;
            }
          }
        },
        legend: {
          show: false
        },
        grid: {
          borderColor: '#f0f0f0',
          strokeDashArray: 3
        }
      };
    },
    
    distributionChartOptions() {
      return {
        chart: {
          type: 'donut',
          height: 300
        },
        colors: ['#1890ff', '#52c41a', '#faad14', '#f5222d', '#722ed1'],
        labels: this.distributionChartData.labels,
        dataLabels: {
          enabled: true,
          formatter: (val) => {
            return Number(val).toFixed(1) + '%';
          }
        },
        plotOptions: {
          pie: {
            donut: {
              size: '60%'
            }
          }
        },
        tooltip: {
          y: {
            formatter: (value) => {
              return value.toLocaleString() + ` ${this.currency}`;
            }
          }
        },
        legend: {
          position: 'bottom'
        }
      };
    },
    
    miniChartOptions() {
      // Get dynamic labels for the mini chart
      let labels = [];
      if (this.monthMapping && this.monthMapping.labels && this.monthMapping.labels.length > 0) {
        labels = this.monthMapping.labels;
      } else {
        // Fallback to static months
        const moment = require('moment');
        labels = moment.months().map(month => month.substring(0, 3));
      }
      
      return {
        chart: {
          type: 'line',
          height: 60,
          toolbar: {
            show: false
          },
          sparkline: {
            enabled: true
          }
        },
        colors: ['#1890ff'],
        stroke: {
          curve: 'smooth',
          width: 2
        },
        fill: {
          type: 'gradient',
          gradient: {
            shadeIntensity: 1,
            opacityFrom: 0.7,
            opacityTo: 0.3,
            stops: [0, 90, 100]
          }
        },
        markers: {
          size: 0
        },
        xaxis: {
          categories: labels,
          labels: {
            show: false
          },
          axisBorder: {
            show: false
          },
          axisTicks: {
            show: false
          }
        },
        yaxis: {
          labels: {
            show: false
          }
        },
        grid: {
          show: false
        },
        tooltip: {
          enabled: false
        }
      };
    }
  },
  data() {
    return {
      currency: null,
    };
  },
  created() {
    this.currency = getBuildingCurrency();
  },
  methods: {
    formatCurrency(value) {
      if (!value && value !== 0) {
        return `0 ${this.currency}`;
      }
      if (value >= 1000000) {
        return (value / 1000000).toFixed(1) + `M ${this.currency}`;
      } else if (value >= 1000) {
        return (value / 1000).toFixed(1) + `K ${this.currency}`;
      }
      return value.toLocaleString() + ` ${this.currency}`;
    },
    
    getCaisseTypeName(type) {
      const types = {
        'PRINCIPAL': this.$t('financialStats.principal'),
        'SUBCAISSE': this.$t('financialStats.subcaisse'),
        'BANK': this.$t('financialStats.bank')
      };
      return types[type] || type;
    },
    
    getCaisseTypeIcon(type) {
      const icons = {
        'PRINCIPAL': 'bank',
        'SUBCAISSE': 'wallet',
        'BANK': 'credit-card'
      };
      return icons[type] || 'bank';
    },
    
    getCaisseTypeClass(type) {
      const classes = {
        'PRINCIPAL': 'type-principal',
        'SUBCAISSE': 'type-subcaisse',
        'BANK': 'type-bank'
      };
      return classes[type] || '';
    },
    
    getPerformancePercent(caisse) {
      const caisses = this.data && this.data.caisses || [];
      if (caisses.length === 0) return 0;
      const maxRevenue = Math.max(...caisses.map(c => c.totalRevenue));
      return maxRevenue > 0 ? (caisse.totalRevenue / maxRevenue * 100).toFixed(1) : 0;
    },
    
    getPerformanceColor(caisse) {
      const percent = this.getPerformancePercent(caisse);
      if (percent >= 80) return '#52c41a';
      if (percent >= 60) return '#1890ff';
      if (percent >= 40) return '#faad14';
      return '#ff4d4f';
    },
    
    getPerformanceText(caisse) {
      const percent = this.getPerformancePercent(caisse);
      if (percent >= 80) return this.$t('financialStats.excellent');
      if (percent >= 60) return this.$t('financialStats.good');
      if (percent >= 40) return this.$t('financialStats.fair');
      return this.$t('financialStats.poor');
    },
    
    getMiniChartData(monthlyRevenue) {
      if (!monthlyRevenue || monthlyRevenue.length === 0) {
        return {
          series: []
        };
      }
      
      // Use dynamic month labels if available
      let labels = [];
      if (this.monthMapping && this.monthMapping.labels && this.monthMapping.labels.length > 0) {
        labels = this.monthMapping.labels;
      } else {
        // Fallback to static months
        const moment = require('moment');
        labels = moment.months().map(month => month.substring(0, 3));
      }
      
      return {
        series: [{ name: 'Revenue', data: monthlyRevenue }],
        labels: labels
      };
    },
    

  }
};
</script>

<style lang="scss" scoped>
@import '../styles/shared.scss';

.caisse-analytics {
  .caisse-info {
    display: flex;
    align-items: center;
    
    .caisse-icon {
      width: 32px;
      height: 32px;
      border-radius: 8px;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-right: 12px;
      font-size: 16px;
      
      &.type-principal {
        background: #e6f7ff;
        color: #1890ff;
      }
      
      &.type-subcaisse {
        background: #f6ffed;
        color: #52c41a;
      }
      
      &.type-bank {
        background: #fff7e6;
        color: #faad14;
      }
    }
    
    .caisse-details {
      .caisse-name {
        font-weight: 500;
        color: #262626;
        margin-bottom: 2px;
      }
      
      .caisse-type {
        font-size: 12px;
        color: #8c8c8c;
      }
    }
  }
  
  .amount-value {
    font-weight: 600;
    color: #52c41a;
  }
  
  .performance-indicator {
    .performance-text {
      font-size: 10px;
      color: #8c8c8c;
      text-align: center;
      margin-top: 4px;
    }
  }
  
  .mini-chart {
    .mini-chart-container {
      :global(.ct-series-a) {
        .ct-line, .ct-area {
          stroke: #1890ff;
          fill: #1890ff;
          fill-opacity: 0.1;
        }
      }
    }
  }
  
  .chart-container {
    :global(.ct-series-a) {
      .ct-bar {
        fill: #1890ff;
      }
    }
  }
}
</style>
